import { BellOff, BellRing, Volume2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { ApiError } from '../../api/envelope';
import type {
  CrmNotificationType,
  NotificationPreferences,
  NotificationScope,
} from '../../api/types';
import { Toggle } from '../../components/Toggle';
import { canUseCalls, inboxGrant } from '../../lib/capabilities';
import {
  channelReach,
  effectiveScope,
  reachLine,
  silentSummary,
  summarizeReach,
  type ChannelReach,
} from '../../lib/notificationReach';
import { useTimeFormat } from '../../lib/tenantTime';
import {
  ALERT_TONES,
  DEFAULT_CALL_RINGTONE,
  DEFAULT_MESSAGE_TONE,
  isAlertTone,
  playTone,
  primeTone,
  type AlertTone,
} from '../../lib/sound';
import { useComms } from './context';
import { NOTIFICATION_TYPE_LABELS, useNotifications } from './notifications';

const TYPE_ORDER: CrmNotificationType[] = [
  'new_message',
  'conversation_assigned',
  'bot_handoff',
  'conversation_created',
];

/**
 * The two answers to `scope`. Long labels for the global picker, which has the
 * room to say what it means; the per-channel column reuses the same values with
 * short labels, because the column head and the section copy already have.
 *
 * There is no Off. A channel you can read always tells you about your own work —
 * a whole line gone silent is how a conversation handed to somebody went unseen —
 * so the quietest answer is Only mine, and the loud one is the default.
 */
const SCOPE_OPTIONS: { value: NotificationScope; label: string; short: string }[] = [
  { value: 'all', label: 'All — every conversation in the channel', short: 'All' },
  { value: 'assigned', label: 'Only mine — assigned to me, and unassigned', short: 'Only mine' },
];

/**
 * One numbered step of the chain, because the chain is the thing nobody could
 * see. Three gates decide whether a notification happens — which conversations
 * reach you, which events on them count, and how you are told — and they are
 * asked in that order and only that order. Presented as one flat list of
 * controls (which is what this card was) they look like alternatives, so an
 * agent who turned "New messages" on and heard nothing had no way to learn that
 * a channel two screens down was set to Only mine.
 */
function Step({
  n,
  title,
  hint,
  children,
}: {
  n: number;
  title: string;
  hint: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="nset-step">
      <div className="nset-step-head">
        <span className="nset-step-n" aria-hidden="true">
          {n}
        </span>
        <span className="nset-step-titles">
          <span className="nset-step-title">{title}</span>
          <span className="nset-step-hint">{hint}</span>
        </span>
      </div>
      <div className="nset-step-body">{children}</div>
    </section>
  );
}

const PUSH_COPY: Record<string, string> = {
  default: 'Off — the browser has not been asked yet.',
  granted: 'On. Banners appear while this tab is in the background, and pushes keep arriving after you close it.',
  denied: 'Blocked in this browser. Re-allow notifications for this site in the address-bar site settings.',
};

/**
 * Settings → Notifications: which conversations reach you at all, per-type
 * toggles for the events on them (server-merged over defaults), the alert
 * sounds, desktop notifications, and the agent's mutes — list, unmute, and
 * muting a whole channel from here. Everything is personal to the signed-in
 * agent; the preferences themselves live in NotificationsProvider, because the
 * toast and the ringtone read the same values.
 */
export function NotificationSettingsCard() {
  const { fmtDateTime } = useTimeFormat();
  const { inboxes, capabilities } = useComms();
  const {
    denied,
    hostedByDashboard,
    mutes,
    unmute,
    preferences,
    savePreferences,
    inboxPreferences,
    saveInboxPreferences,
    clearInboxPreferences,
    supportsPush,
    pushPermission,
    enablePush,
  } = useNotifications();
  const [error, setError] = useState<string | null>(null);

  if (denied) return null;

  // Channel mutes live in the per-channel table now; only conversation-scoped
  // rows still need a list of their own.
  const conversationMutes = mutes.filter((row) => row.scope === 'conversation');

  /**
   * What each channel will ACTUALLY do, resolved the way the server resolves it.
   * The controls below show what is set; this shows what that adds up to, which
   * is a different question once access, membership and ownership are in play —
   * none of which appear on this screen as a control at all.
   */
  const reachByInbox = new Map<number, ChannelReach>(
    inboxes.map((inbox) => [
      inbox.id,
      channelReach({
        inboxId: inbox.id,
        grant: inboxGrant(capabilities, inbox.id),
        preferences,
        override: inboxPreferences[String(inbox.id)] ?? {},
      }),
    ]),
  );

  const summary = summarizeReach([...reachByInbox.values()]);
  const silentNote = silentSummary(summary);
  const inboxName = (id: number) => inboxes.find((inbox) => inbox.id === id)?.name ?? `#${id}`;

  const saveChannel = (inboxId: number, patch: Partial<NotificationPreferences>) => {
    setError(null);
    saveInboxPreferences(inboxId, patch).catch(() =>
      setError('Could not save that channel setting.'),
    );
  };

  const save = (patch: Partial<NotificationPreferences>) => {
    setError(null);
    savePreferences(patch).catch((e: unknown) => {
      setError(e instanceof ApiError ? e.message : 'Could not save that.');
    });
  };

  const setType = (type: CrmNotificationType, next: boolean) => save({ [type]: next });

  const scope = effectiveScope(preferences);
  // Only offered when calls could actually reach them; a switch for something
  // that never happens is noise on the one screen meant to reduce it.
  const hasCalls = canUseCalls(capabilities);
  /**
   * Following the assistant's own conversations is the chatbot product, so it
   * is offered to whoever holds that permission and to nobody else. The server
   * checks the same thing on every send — a toggle set before the permission
   * went away must stop delivering, and a preference is not a place to keep
   * access.
   */
  const hasBot = capabilities?.access?.features?.bot === 'manage';
  const followBotChats = preferences?.follow_bot_chats === true;
  const ringOn = preferences?.calls_enabled !== false;

  const soundOn = preferences?.sound_enabled !== false;
  const messageTone = isAlertTone(preferences?.message_tone)
    ? preferences.message_tone
    : DEFAULT_MESSAGE_TONE;
  const callRingtone = isAlertTone(preferences?.call_ringtone)
    ? preferences.call_ringtone
    : DEFAULT_CALL_RINGTONE;

  const tonePicker = (
    label: string,
    hint: string,
    value: AlertTone,
    onPick: (next: AlertTone) => void,
  ) => (
    <div className="nset-tone">
      <div className="nset-tone-label">
        <span className="nset-tone-title">{label}</span>
        <span className="nset-tone-hint">{hint}</span>
      </div>
      <select
        className="nset-select"
        value={value}
        disabled={!preferences || !soundOn}
        onChange={(e) => {
          const next = e.target.value as AlertTone;
          // A change event IS a user gesture — the only moment Safari lets us
          // unlock this element, and it has never been played before.
          primeTone(next);
          onPick(next);
        }}
      >
        {ALERT_TONES.map((tone) => (
          <option key={tone.value} value={tone.value}>
            {tone.label}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="cbtn nset-tone-play"
        title={`Play ${value}`}
        disabled={!soundOn}
        onClick={() => playTone(value)}
      >
        <Volume2 size={13} />
        Play
      </button>
    </div>
  );

  return (
    <div className="cset-matrix nset">
      <div className="cset-matrix-title">Notifications</div>
      <div className="nset-sub">
        {hostedByDashboard ? (
          <>
            What notifies <b>you</b> — a toast on whichever dashboard page you are on, listed
            under <b>CRM</b> in the dashboard’s notifications, plus a push when the dashboard is
            closed. Nothing here changes what you can see or do.
          </>
        ) : (
          <>
            What notifies <b>you</b> — a toast here and in the dashboard, plus a push when the
            dashboard is closed. Nothing here changes what you can see or do.
          </>
        )}
      </div>

      {error && <div className="comms-error-note">{error}</div>}

      {/*
        The answer, before the controls that produce it. Everything below is an
        input; this is the outcome, and it is the only place the gates that are
        NOT on this screen — channel access, membership, ownership — become
        visible. Without it a channel can carry four ticked boxes and still never
        notify anyone, with nothing on screen to say so.
      */}
      <div className="nset-reach">
        <div className="nset-reach-head">
          <BellRing size={13} />
          Where notifications reach you
        </div>
        {inboxes.length === 0 ? (
          <div className="nset-reach-note">No channels yet.</div>
        ) : summary.live.length === 0 ? (
          <div className="nset-reach-note">
            Nothing reaches you right now{silentNote ? ` — ${silentNote}` : ''}. The rest of
            this card is what changes that.
          </div>
        ) : (
          <>
            <div className="nset-reach-chips">
              {summary.live.map((row) => (
                <span key={row.inboxId} className="nset-reach-chip" title={reachLine(row)}>
                  {inboxName(row.inboxId)}
                  {row.width === 'mine' && <span className="nset-reach-mark">only mine</span>}
                </span>
              ))}
            </div>
            {silentNote && (
              <div className="nset-reach-note">
                Silent channels: {silentNote}.
              </div>
            )}
          </>
        )}
      </div>

      {/*
        Step 1 of 3 — see `Step`. The blanket default and the per-channel table
        are ONE question asked at two scopes, and they used to sit at opposite
        ends of this card with five unrelated blocks between them: the default
        first, the table last, and nothing saying they were the same control.
      */}
      <Step
        n={1}
        title="Which conversations reach you"
        hint={
          <>
            <b>All</b> is every conversation on the channel, whoever it is assigned to.{' '}
            <b>Only mine</b> is the ones assigned to you, plus the unassigned — so a new
            arrival still reaches you before anyone claims it. There is no off: a channel you
            can read always tells you about your own work. You keep every bit of access
            either way — this changes what you hear about, never what you can see or do.
          </>
        }
      >
        <div className="nset-scope">
          <div className="nset-scope-label">
            <span className="nset-scope-title">Every channel, unless set below</span>
            <span className="nset-scope-hint">
              Applies to channels connected later too.
            </span>
          </div>
          <select
            className="nset-select"
            aria-label="Default notification reach"
            value={scope}
            disabled={!preferences}
            onChange={(e) => save({ scope: e.target.value as NotificationScope })}
          >
            {SCOPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="nset-chan-table">
          <div className="nset-chan-head">
            <span>Channel</span>
            <span>Notify me about</span>
            <span />
          </div>
          {inboxes.length === 0 && <div className="nset-empty">No channels yet.</div>}
          {inboxes.map((inbox) => {
            const override = inboxPreferences[String(inbox.id)] ?? {};
            const overridden = override.scope !== undefined;
            const reach = reachByInbox.get(inbox.id);
            // The server refuses a preference write for a channel outside the
            // agent's access (1002), so the control would only ever produce an
            // error toast. The row still lists, and says why.
            const noAccess = reach?.blocked === 'no-access';
            const channelScope = effectiveScope(preferences, override);
            return (
              <div key={inbox.id} className={`nset-chan-row${reach?.blocked ? ' is-silent' : ''}`}>
                <span className="nset-chan-name">
                  <span className="nset-chan-titlerow">
                    <span className="nset-chan-title">{inbox.name}</span>
                    {noAccess && <span className="nset-chan-tag">no access</span>}
                    {reach?.blocked === 'all-off' && <span className="nset-chan-tag">all off</span>}
                    {!reach?.blocked && overridden && <span className="nset-chan-tag">custom</span>}
                  </span>
                  {/* Only where the outcome is not the ordinary one — see
                      ChannelReach.notable. The column says the rest. */}
                  {reach?.notable && <span className="nset-chan-reach">{reachLine(reach)}</span>}
                </span>
                <select
                  className="nset-select nset-chan-select"
                  aria-label={`Notifications for ${inbox.name}`}
                  value={channelScope}
                  disabled={!preferences || noAccess}
                  onChange={(e) =>
                    saveChannel(inbox.id, { scope: e.target.value as NotificationScope })
                  }
                >
                  {SCOPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.short}
                    </option>
                  ))}
                </select>
                <span className="nset-chan-actions">
                  {overridden && (
                    <button
                      type="button"
                      className="cbtn nset-chan-btn"
                      title="Follow my default again"
                      onClick={() =>
                        void clearInboxPreferences(inbox.id).catch(() =>
                          setError('Could not reset that channel.'),
                        )
                      }
                    >
                      Reset
                    </button>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </Step>

      <Step
        n={2}
        title="What counts as worth telling you"
        hint="On a conversation that already reaches you, these are the things that raise a notification. Everything else stays in the inbox until you look."
      >
        <div className="nset-toggles">
          {TYPE_ORDER.map((type) => (
            <Toggle
              key={type}
              checked={preferences?.[type] ?? false}
              disabled={!preferences}
              onChange={(next) => setType(type, next)}
              title={NOTIFICATION_TYPE_LABELS[type].title}
              sub={NOTIFICATION_TYPE_LABELS[type].sub}
            />
          ))}
        </div>

        {hasBot && (
          <Toggle
            checked={followBotChats}
            disabled={!preferences}
            onChange={(next) => save({ follow_bot_chats: next })}
            title="Follow the assistant's conversations"
            sub="Off by default: conversations the assistant is answering stay off your desk until it hands one over. Turn this on to be notified about them too."
          />
        )}
      </Step>

      <Step
        n={3}
        title="How you hear about it"
        hint="Where a notification lands once it has passed the two questions above."
      >
        <div className="nset-sounds">
          {hasCalls && (
            <Toggle
              checked={ringOn}
              disabled={!preferences}
              onChange={(next) => save({ calls_enabled: next })}
              title="Ring me for calls"
              sub="Turn this off to stop incoming calls ringing here. It does not take your calling access away — you can still place one."
            />
          )}
          <Toggle
            checked={soundOn}
            disabled={!preferences}
            onChange={(next) => save({ sound_enabled: next })}
            title="Play a sound"
            sub="Alert tones for notifications and the ringtone for incoming calls."
          />
          {tonePicker('Notification tone', 'Plays once per notification.', messageTone, (next) =>
            save({ message_tone: next }),
          )}
          {tonePicker(
            'Incoming call ringtone',
            'Loops while a call is ringing.',
            callRingtone,
            (next) => save({ call_ringtone: next }),
          )}
        </div>

        {supportsPush && (
          <div className="nset-push">
            <span className="nset-push-state">
              <b>Desktop notifications.</b> {PUSH_COPY[pushPermission] ?? ''}
            </span>
            {pushPermission === 'default' && (
              <button type="button" className="cbtn" onClick={() => void enablePush()}>
                <BellRing size={13} />
                Turn on
              </button>
            )}
          </div>
        )}
      </Step>

      {/* Not a step: these are one-off exceptions an agent made from a thread,
          listed here so they can be undone from one place. */}
      <div className="nset-mutehead">
        <BellOff size={13} />
        Conversations you muted or are watching
      </div>
      {conversationMutes.length === 0 && (
        <div className="nset-empty">
          Nothing muted. Mute or watch a single conversation from its own menu.
        </div>
      )}
      {conversationMutes.map((row) => (
        <div key={row.id} className="nset-mute">
          <span className="nset-mute-what">
            {row.mode === 'watch' ? 'Watching' : 'Muted'} · Conversation #
            {row.conversationDisplayId}
          </span>
          <span className="nset-mute-until">
            {row.mutedUntil ? `until ${fmtDateTime(row.mutedUntil)}` : 'until you change it'}
          </span>
          <button
            type="button"
            className="cbtn nset-mute-remove"
            onClick={() => void unmute(row.id).catch(() => setError('Could not undo that.'))}
          >
            <Volume2 size={13} />
            {row.mode === 'watch' ? 'Stop watching' : 'Unmute'}
          </button>
        </div>
      ))}
    </div>
  );
}
